// Story 1.25 — TanStack useMutation for POST
// /api/v1/portfolios/applications/:id/withdraw.
//
// Translates the 2 new backend error codes (404 / 409 with
// current_status echoed) into UX-friendly toasts. Invalidates BOTH
// /account/creator-portfolios queries on success so the my-apps
// list re-renders the new 'withdrawn' status AND the browse list's
// already_applied flag flips back to false (creator can re-apply
// from the same page without a manual refetch).

import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { attachRequestIdToScope } from "../../lib/api-client";

export type WithdrawPortfolioApplicationInput = {
  portfolio_application_id: string;
};

export type WithdrawPortfolioApplicationData = {
  portfolio_application_id: string;
  portfolio_id: string;
  status: "withdrawn";
  withdrawn_at: string;
};

export type WithdrawPortfolioApplicationResponse = {
  data: WithdrawPortfolioApplicationData;
};

export class WithdrawPortfolioApplicationError extends Error {
  readonly code: string;
  readonly status: number;
  readonly currentStatus?: string;
  readonly portfolioApplicationId?: string;

  constructor(args: {
    code: string;
    message: string;
    status: number;
    currentStatus?: string;
    portfolioApplicationId?: string;
  }) {
    super(args.message);
    this.name = "WithdrawPortfolioApplicationError";
    this.code = args.code;
    this.status = args.status;
    this.currentStatus = args.currentStatus;
    this.portfolioApplicationId = args.portfolioApplicationId;
  }
}

async function withdrawPortfolioApplication(
  input: WithdrawPortfolioApplicationInput,
): Promise<WithdrawPortfolioApplicationResponse> {
  const response = await fetch(
    `/api/v1/portfolios/applications/${encodeURIComponent(
      input.portfolio_application_id,
    )}/withdraw`,
    { method: "POST", credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let payload: Record<string, unknown> = {};
    try {
      payload = (await response.json()) as Record<string, unknown>;
    } catch {
      // body not JSON
    }
    const err = (payload?.error ?? {}) as Record<string, unknown>;
    throw new WithdrawPortfolioApplicationError({
      code: (err.code as string) ?? "UNKNOWN",
      message:
        (err.message as string) ??
        `Withdraw failed (HTTP ${response.status})`,
      status: response.status,
      currentStatus: err.current_status as string | undefined,
      portfolioApplicationId: err.portfolio_application_id as
        | string
        | undefined,
    });
  }
  return (await response.json()) as WithdrawPortfolioApplicationResponse;
}

export function useWithdrawPortfolioApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: withdrawPortfolioApplication,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["creator-portfolios", "recruiting"],
      });
      queryClient.invalidateQueries({
        queryKey: ["creator-portfolios", "my-applications"],
      });
    },
    onError: (error) => {
      if (error instanceof WithdrawPortfolioApplicationError) {
        switch (error.code) {
          case "PORTFOLIO_APPLICATION_NOT_FOUND":
            toast.error(
              "Application not found. It may have already been processed.",
            );
            break;
          case "PORTFOLIO_APPLICATION_NOT_WITHDRAWABLE":
            toast.error(
              `Only pending applications can be withdrawn (status: ${
                error.currentStatus ?? "?"
              }).`,
            );
            break;
          case "RATE_LIMITED":
            toast.error(
              "You've hit the withdraw rate limit. Try again in a few minutes.",
            );
            break;
          case "CREATOR_PROFILE_FORBIDDEN":
            toast.error("Approved-creator only.");
            break;
          default:
            toast.error(error.message);
        }
      } else {
        toast.error("Something went wrong. Try again.");
      }
    },
  });
}
